import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogBody,
  DialogProps,
  DialogTitle,
  DialogBodyProps,
  DialogTitleProps,
  DialogActions as ModalActions,
  DialogDescription as ModalDescription
} from '@anyui/core';
import { cn } from '@anytag/shared-utils';
import { Button, ButtonProps } from '../button-v2';

/**
 * NOTE:
 * We have some customizations for the modal components
 * so we should use from this exported components instead of using directly from @anyui/core
 */

/**
 * Customized Modal component
 */
export type ModalProps = Omit<DialogProps, 'size'> & { size?: DialogProps['size'] | 'xl' | 'content' };
export const Modal = ({ size, className, classNames, ...restProps }: ModalProps) => {
  const customeClassNames = {
    ...classNames,
    container: cn('pt-0', classNames?.container),
    // anyui just provides 2 sizes `sm` and `md`, this is may enough for them but not for each product so we can have more custom sizes ourselves
    panel: cn(
      'max-h-[calc(100vh-4rem)] overflow-auto', // overflow height
      '[&_.form-provider_[data-slot="dialog-actions"]]:mt-1 [&_.form-provider_[data-slot="dialog-body"]]:p-0', // form inside modal
      classNames?.panel
    )
  };
  const customSizeProps = (
    !!size && ['xl', 'content'].includes(size)
      ? {
          classNames: {
            ...customeClassNames,
            panel: cn(
              // More custom sizes
              {
                'max-w-229': size === 'xl', // 916px
                'max-w-max': size === 'content'
                // add more custom sizes here
              },
              customeClassNames.panel
            )
          }
        }
      : { size: size as DialogProps['size'], classNames: { ...customeClassNames } }
  ) satisfies Partial<DialogProps>;

  return <Dialog className={cn('*:z-10', className)} {...customSizeProps} {...restProps} />;
};

/**
 * Customized Modal footer component, includes 2 default cancel and submit buttons.
 */
export interface ModalFooterProps {
  className?: string;
  children?: React.ReactNode;
  cancelButton?: ButtonProps;
  submitButton?: ButtonProps;
}
export const ModalFooter = ({ cancelButton, submitButton, children, className }: ModalFooterProps) => {
  const { t } = useTranslation();

  return (
    <ModalActions className={className}>
      <div className="flex-1">{children}</div>
      {cancelButton ? <Button variant="outline" title={t('Button.Cancel')} {...cancelButton} /> : null}
      {submitButton ? <Button variant="solid" title={t('Button.OK')} {...submitButton} /> : null}
    </ModalActions>
  );
};

export const ModalBody = ({ className, ...restProps }: DialogBodyProps) => (
  <DialogBody className={cn('mt-0 pt-0', className)} {...restProps} />
);

export const ModalTitle = ({ className, ...restProps }: DialogTitleProps) => (
  <DialogTitle className={cn('mb-4', className)} {...restProps} />
);

// Export other uncustomized modal components from @anyui/core
export { ModalDescription, ModalActions };
